import { Helmet } from 'react-helmet-async';
import { SITE } from '../../utils/seo';

/**
 * SEOHead — renders meta tags, OG, Twitter Cards, and JSON-LD for a page.
 *
 * Props:
 *   title       - page title (appended with site name)
 *   description - page description
 *   canonical   - full canonical URL
 *   ogType      - og:type (default: 'website')
 *   noIndex     - set true to block indexing
 *   jsonLd      - array of Schema.org objects to inject as JSON-LD
 */
export function SEOHead({
  title,
  description = SITE.description,
  canonical,
  ogType = 'website',
  noIndex = false,
  jsonLd = [],
}) {
  const pageTitle = title ? `${title} — ${SITE.name}` : SITE.name;
  const pageUrl = canonical || SITE.url;

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={pageUrl} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:site_name" content={SITE.name} />
      <meta property="og:locale" content={SITE.locale} />

      {/* Twitter / X Cards */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:site" content={SITE.twitterHandle} />
      <meta name="twitter:creator" content={SITE.twitterHandle} />

      {/* JSON-LD structured data */}
      {jsonLd.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}
